import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { trpcClient } from '@/lib/trpc';
import { RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface SyncStatusPillProps {
  entityType: string;
  entityId: string;
  onSyncSuccess?: () => void;
  className?: string;
  compact?: boolean;
}

export function SyncStatusPill({
  entityType,
  entityId,
  onSyncSuccess,
  className,
  compact = false,
}: SyncStatusPillProps) {
  const queryClient = useQueryClient();

  // Query trạng thái job mới nhất của entity
  const { data: latestJob, refetch } = useQuery({
    queryKey: ['sync-job', entityType, entityId],
    queryFn: () => trpcClient.jobs.getLatestEntityJob.query({ entityType, entityId }),
    enabled: !!entityId && entityId !== 'new',
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'RUNNING' || status === 'QUEUED' ? 2000 : false;
    },
  });

  const triggerMutation = useMutation({
    mutationFn: () =>
      trpcClient.jobs.triggerSync.mutate({
        entityType,
        entityId,
        syncType: 'DATA_SYNC',
      }),
    onSuccess: (res) => {
      toast.info(res.message || 'Đang tiến hành đồng bộ ngầm...');
      queryClient.invalidateQueries({ queryKey: ['sync-job', entityType, entityId] });
      setTimeout(() => {
        refetch();
        onSyncSuccess?.();
      }, 1600);
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Lỗi khi kích hoạt đồng bộ');
    },
  });

  const isSyncing = latestJob?.status === 'RUNNING' || latestJob?.status === 'QUEUED' || triggerMutation.isPending;

  const handleSync = () => {
    if (isSyncing) return;
    triggerMutation.mutate();
  };

  if (!entityId || entityId === 'new') return null;

  if (isSyncing) {
    return (
      <div className={cn('inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md shadow-xs animate-pulse', className)}>
        <RefreshCw className="h-3.5 w-3.5 animate-spin text-amber-600" />
        <span>{compact ? 'Đang sync...' : 'Đang đồng bộ ngầm...'}</span>
      </div>
    );
  }

  if (latestJob?.status === 'FAILED') {
    return (
      <div className={cn('inline-flex items-center gap-1.5', className)}>
        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded-md shadow-xs" title={latestJob.error || 'Lỗi xử lý'}>
          <AlertCircle className="h-3.5 w-3.5 text-rose-600" />
          <span>Lỗi sync</span>
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={handleSync}
          className="h-6 px-1.5 text-[11px] text-rose-600 hover:text-rose-700 border-rose-200 hover:bg-rose-50"
        >
          Thử lại
        </Button>
      </div>
    );
  }

  // Khi ở trạng thái bình thường/nhàn rỗi (idle hoặc completed): Không hiển thị nút Đồng bộ thừa thãi
  return null;
}

export default SyncStatusPill;
